import { ModalDialog } from './ModalFrame';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import type { HostingServerView } from '@tosho/contracts';
import { ModalCloseButton } from './ModalCloseButton';
import { PrototypeIcon } from './PrototypeIcon';
import { HOSTING_PANELS, declaredHostingPanels } from './hosting-technology-catalogue';
import {
  hostingServerApi,
  hostingServerError,
  HostingServerRequestError,
} from './hosting-server-api';

/** Accès manuel : un lien et du texte seulement. Le même CAS que la fiche protège
 * les changements concurrents ; aucune instruction n'est interprétée ou exécutée.
 */
export function HostingCockpitAccessDialog({
  server,
  csrfToken,
  onClose,
}: {
  readonly server: HostingServerView;
  readonly csrfToken: string;
  readonly onClose: () => void;
}): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null),
    alive = useRef(true),
    title = useId();
  const [current, setCurrent] = useState(server);
  const initial = declaredHostingPanels(server).find((panel) => panel.kind === 'COCKPIT');
  const [url, setUrl] = useState(initial?.accessUrl ?? ''),
    [comment, setComment] = useState(initial?.accessComment ?? ''),
    [busy, setBusy] = useState(false),
    [locked, setLocked] = useState(false),
    [error, setError] = useState<string | null>(null),
    [feedback, setFeedback] = useState<string | null>(null);
  const savedUrl = declaredHostingPanels(current).find(
    (panel) => panel.kind === 'COCKPIT',
  )?.accessUrl;
  useEffect(() => {
    alive.current = true;
    const previous = document.activeElement,
      element = dialog.current;
    element?.showModal();
    return () => {
      alive.current = false;
      element?.close();
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);
  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy || locked) return;
    setBusy(true);
    setError(null);
    setFeedback(null);
    try {
      const controlPanels = declaredHostingPanels(current).map((panel) =>
        panel.kind === 'COCKPIT'
          ? { ...panel, accessUrl: url.trim() || null, accessComment: comment || null }
          : panel,
      );
      const result = await hostingServerApi.describe(
        current,
        { ...current, controlPanels },
        csrfToken,
      );
      if (alive.current) {
        setCurrent(result);
        setFeedback('Réglages Cockpit enregistrés.');
      }
    } catch (failure) {
      if (alive.current) {
        setError(hostingServerError(failure));
        if (
          failure instanceof HostingServerRequestError &&
          failure.code === 'HOSTING_SERVER_CONFLICT'
        )
          setLocked(true);
      }
    } finally {
      if (alive.current) setBusy(false);
    }
  }
  return (
    <ModalDialog
      ref={dialog}
      className="hosting-servers-editor hosting-panel-access"
      aria-labelledby={title}
      aria-busy={busy}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onClose();
      }}
      close={<ModalCloseButton className="tosho-modal-close" disabled={busy} onClick={onClose} />}
    >
      <form onSubmit={(event) => void submit(event)}>
        <header className="tosho-modal-header">
          <div>
            <p className="hosting-servers-editor__eyebrow">Accès au serveur</p>
            <h2 id={title}>Réglages Cockpit</h2>
          </div>
        </header>
        <div className="hosting-panel-access__identity">
          <img src={HOSTING_PANELS.COCKPIT.logo} alt="Cockpit" width="168" height="48" />
          <strong>{server.label}</strong>
        </div>
        {error && (
          <p className="tosho-feedback is-error" role="alert">
            {error}
          </p>
        )}
        {feedback && (
          <p className="tosho-feedback is-success" role="status">
            {feedback}
          </p>
        )}
        <fieldset disabled={busy || locked}>
          <label>
            URL de Cockpit
            <input
              name="accessUrl"
              type="url"
              maxLength={2048}
              value={url}
              placeholder="https://localhost:9090/"
              onChange={(event) => setUrl(event.target.value)}
            />
          </label>
          <label>
            Commentaires et instructions d’accès
            <textarea
              name="accessComment"
              rows={8}
              maxLength={4000}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
            />
          </label>
        </fieldset>
        <p className="hosting-panel-access__help">
          Si un tunnel SSH est nécessaire, ouvrez-le sur votre ordinateur avant Cockpit.
        </p>
        <footer>
          <button type="button" disabled={busy} onClick={onClose}>
            Fermer
          </button>
          {savedUrl && (
            <a
              className="hosting-panel-access__open"
              href={savedUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <PrototypeIcon name="arrow-right" /> Ouvrir Cockpit
            </a>
          )}
          <button type="submit" className="hosting-servers__primary" disabled={busy || locked}>
            {busy ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </footer>
      </form>
    </ModalDialog>
  );
}
